import { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

interface AccessibilityPreferences {
  dyslexiaFont?: boolean;
  textSize?: "default" | "large" | "extra-large";
  lineSpacing?: boolean;
  highContrast?: boolean;
  reducedMotion?: boolean;
}

interface AccessibilityContextType {
  preferences: AccessibilityPreferences;
  isLoading: boolean;
}

const AccessibilityContext = createContext<AccessibilityContextType>({
  preferences: {},
  isLoading: false,
});

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery<{ preferences: AccessibilityPreferences }>({
    queryKey: ["/api/profile/accessibility"],
    enabled: isAuthenticated,
  });

  const preferences = data?.preferences || {};

  useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle("a11y-dyslexia-font", !!preferences.dyslexiaFont);
    root.classList.toggle("a11y-large-text", preferences.textSize === "large");
    root.classList.toggle("a11y-extra-large-text", preferences.textSize === "extra-large");
    root.classList.toggle("a11y-line-spacing", !!preferences.lineSpacing);
    root.classList.toggle("a11y-high-contrast", !!preferences.highContrast);
    root.classList.toggle("a11y-reduced-motion", !!preferences.reducedMotion);

    return () => {
      root.classList.remove(
        "a11y-dyslexia-font",
        "a11y-large-text",
        "a11y-extra-large-text",
        "a11y-line-spacing",
        "a11y-high-contrast",
        "a11y-reduced-motion"
      );
    };
  }, [preferences]);

  return (
    <AccessibilityContext.Provider value={{ preferences, isLoading }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
